import moment from 'moment-timezone'
import { LocationService } from '@/services/locationService'
import { OrderSettingsService } from '@/services/orderSettingsService'
import { OrderService } from '@/services/orderService'

/**
 * Checks if there is any available pickup time before the special closure begins.
 *
 * @param {string} date - The date to check, in "YYYY-MM-DD" format.
 * @param {moment.Moment} closureStart - Moment object representing the start of the closure.
 * @param {Array<{ begin: string }>} standardOpeningHours - Array of standard opening hours with 'begin' time formatted as "hh:mm".
 * @param {string} timezone - The timezone string (e.g., 'Europe/Paris').
 * @returns {boolean} - True if at least one standard opening time is before the closure starts.
 */
function hasTimeBeforeClosure(date, closureStart, standardOpeningHours, timezone) {
  return standardOpeningHours.some((hour) => {
    const [hours, minutes] = hour.begin.split(':').map((hourOrMinutes) => parseInt(hourOrMinutes, 10))
    const standardOpeningBeginTime = moment.tz(`${date} ${hours}:${minutes}`, 'YYYY-MM-DD HH:mm', timezone)
    return standardOpeningBeginTime.isBefore(closureStart)
  })
}

/**
 * Checks if there is any available pickup time after the special closure ends.
 *
 * @param {string} date - The date to check, in "YYYY-MM-DD" format.
 * @param {moment.Moment} closureEnd - Moment object representing the end of the closure.
 * @param {Array<{ end: string }>} standardOpeningHours - Array of standard opening hours with 'end' time formatted as "hh:mm".
 * @param {string} timezone - The timezone string (e.g., 'Europe/Paris').
 * @returns {boolean} - True if at least one standard opening time is after the closure ends.
 */
function hasTimeAfterClosure(date, closureEnd, standardOpeningHours, timezone) {
  return standardOpeningHours.some((hour) => {
    const [hours, minutes] = hour.end.split(':').map((hourOrMinutes) => parseInt(hourOrMinutes, 10))
    const standardOpeningEndTime = moment.tz(`${date} ${hours}:${minutes}`, 'YYYY-MM-DD HH:mm', timezone)
    return standardOpeningEndTime.isAfter(closureEnd)
  })
}

/**
 * Returns a predicate function to determine whether a special closure prevents pickup on a given date.
 *
 * This function compares the checked deadline date with the start and end of a special closure period.
 * It handles three cases:
 * - If the date is the start of the closure, it checks if there is time to pick up before the closure begins.
 * - If the date is the end of the closure, it checks if there is time to pick up after the closure ends.
 * - If the date falls entirely within the closure period, pickup is not allowed.
 *
 * @param {string} checkedDeadlineDate - The date being checked, in 'YYYY-MM-DD' format.
 * @param {Array} standardOpeningDayHours - The standard opening hours for the weekday of the checked date.
 * @param {Object} pickupLocation - The location object containing standard and exceptional opening hours.
 * @param {string} pickupLocation.timezone - The timezone of the store, used for time comparisons.
 * @returns {function(Object): boolean} - A predicate function that takes a special closure object and returns true if it prevents pickup.
 */
function specialClosurePreventsPickup(checkedDeadlineDate, standardOpeningDayHours, pickupLocation) {
  return (specialClosure) => {
    const deadlineDay = moment(checkedDeadlineDate).format('YYYY-MM-DD')
    const closureStart = moment(specialClosure.begin)
    const closureEnd = moment(specialClosure.end)
    const closureStartDay = closureStart.format('YYYY-MM-DD')
    const closureEndDay = closureEnd.format('YYYY-MM-DD')

    const isDuringClosure = closureStartDay <= deadlineDay && deadlineDay <= closureEndDay

    if (isDuringClosure) {
      const deadlineAtClosureStartDay = deadlineDay === closureStartDay
      const deadlineAtClosureEndDay = deadlineDay === closureEndDay

      if (deadlineAtClosureStartDay) {
        return !hasTimeBeforeClosure(
          checkedDeadlineDate,
          closureStart,
          standardOpeningDayHours,
          pickupLocation.timezone
        )
      }

      if (deadlineAtClosureEndDay) {
        return !hasTimeAfterClosure(checkedDeadlineDate, closureEnd, standardOpeningDayHours, pickupLocation.timezone)
      }

      // If the deadline falls strictly between the start and end of the closure period (not on the boundary days),
      // then the pickup is prevented because the location is fully closed on that day.
      return true
    } else {
      return false
    }
  }
}

/**
 * Checks whether any special closures prevent pickup on a given date.
 *
 * This function is only evaluated if the store is normally open on the given date.
 * It uses the `special_closures` array from the pickup location to determine if any
 * special closure rules override the standard opening hours and prevent pickup.
 *
 * @param {string} checkedDeadlineDate - The date being checked, in 'YYYY-MM-DD' format.
 * @param {Object} pickupLocation - The location object containing standard and exceptional opening hours.
 * @param {boolean} storeIsOpened - Indicates whether the store is normally open on the given date.
 * @param {Array} standardOpeningDayHours - The standard opening hours for the weekday of the checked date.
 * @param {string} pickupLocation.timezone - The timezone of the store, used for time comparisons.
 * @param {Array} pickupLocation.special_closures - Array of special closure rules to evaluate.
 * @returns {boolean} - Returns true if a special closure prevents pickup, false otherwise.
 */
function checkSpecialClosures(checkedDeadlineDate, pickupLocation, storeIsOpened, standardOpeningDayHours) {
  let aSpecialClosurePreventsPickup = false
  if (storeIsOpened) {
    aSpecialClosurePreventsPickup = pickupLocation.special_closures.some(
      specialClosurePreventsPickup(checkedDeadlineDate, standardOpeningDayHours, pickupLocation)
    )
  }
  return aSpecialClosurePreventsPickup
}

/**
 * Checks whether the store is open for pickup on a specific date.
 *
 * This function evaluates:
 * - If the store is exceptionally open on the given date (based on `exceptional_opening_hours`).
 * - If the store has standard opening hours on that weekday.
 * - If any special closures override the standard opening hours and prevent pickup.
 *
 * The `storeIsOpened` flag is explicitly converted to a boolean by checking if the standard opening hours array has a length greater than 0,
 * as required by the `checkSpecialClosures` function.
 *
 * @param {string} checkedDeadlineDate - The date being checked, in 'YYYY-MM-DD' format.
 * @param {Object} pickupLocation - The location object containing standard and exceptional opening hours.
 * @param {Array} pickupLocation.exceptional_opening_hours - Array of exceptional opening day objects with `date` and `hours`.
 * @param {string} pickupLocation.timezone - The timezone of the store, used for time comparisons.
 * @param {Array} pickupLocation.special_closures - Array of special closure rules to evaluate.
 * @param {Array} standardOpeningDayHours - The standard opening hours for the weekday of the checked date.
 * @returns {boolean} - Returns true if the store is open for pickup on the given date, false otherwise.
 */
function isStoreOpenForPickup(checkedDeadlineDate, pickupLocation, standardOpeningDayHours) {
  const storeIsExceptionallyOpened = pickupLocation.exceptional_opening_hours.some(
    (exceptionalDay) => exceptionalDay.date.split('T')[0] === checkedDeadlineDate && exceptionalDay.hours?.length
  )
  // check > 0 to have a boolean such as checkSpecialClosures require as parameter
  const storeIsOpened = Array.isArray(standardOpeningDayHours) && standardOpeningDayHours.length > 0
  const aSpecialClosurePreventsPickup = checkSpecialClosures(
    checkedDeadlineDate,
    pickupLocation,
    storeIsOpened,
    standardOpeningDayHours
  )
  return storeIsExceptionallyOpened || (storeIsOpened && !aSpecialClosurePreventsPickup)
}

/**
 * Returns a predicate function to check if a given exceptional day represents a closure.
 *
 * An exceptional closure is identified when the date matches the checked deadline date
 * and the `hours` array is either missing or empty, indicating the store is closed.
 *
 * @param {string} checkedDeadlineDate - The date to check against, in 'YYYY-MM-DD' format.
 * @returns {function(Object): boolean} - A predicate function that returns true if the store is exceptionally closed on that date.
 */
function checkExceptionalClosures(checkedDeadlineDate) {
  return (exceptionalDay) => exceptionalDay.date.split('T')[0] === checkedDeadlineDate && !exceptionalDay.hours?.length
}

/**
 * Validates whether a given deadline date is acceptable for pickup based on store hours and exceptions.
 *
 * This function checks if the selected deadline date:
 * - Is not the same as the current order deadline.
 * - Is not a date when the store is exceptionally closed.
 * - Falls on a day when the store is open for pickups.
 * - Is not at a past date
 *
 * @param {string} checkedDeadlineDate - The date to validate, in 'YYYY-MM-DD' format.
 * @param {Object} pickupLocation - The location object containing standard and exceptional opening hours.
 * @param {Object} pickupLocation.opening_hours - An object mapping weekdays to opening hours.
 * @param {Array} pickupLocation.exceptional_opening_hours - Array of exceptional opening day objects with `date` and `hours`.
 * @param {string} pickupLocation.timezone - The timezone of the store, used for time comparisons.
 * @param {Array} pickupLocation.special_closures - Array of special closure rules to evaluate.
 * @param {string} currentOrderDeadline - The current order's deadline date, in 'YYYY-MM-DD' format.
 * @returns {boolean} - Returns true if the deadline is valid for pickup, false otherwise.
 */
export function checkDeadlineIsValid(checkedDeadlineDate, pickupLocation, currentOrderDeadline) {
  const isAlreadyPickupDate = checkedDeadlineDate === moment(currentOrderDeadline).format('YYYY-MM-DD')
  const deadlineIsInThePast = moment(checkedDeadlineDate).isBefore(moment())
  return !isAlreadyPickupDate && isPickupDateAvailable(checkedDeadlineDate, pickupLocation) && !deadlineIsInThePast
}

/**
 * Checks whether a given date is available for pickup at a specific store location.
 *
 * A date is considered available if:
 * - The store is not exceptionally closed on that date.
 * - The store is open for pickup based on standard or exceptional opening hours.
 *
 * Note: A store can be exceptionally closed, exceptionally open, or neither — but not both at the same time.
 *
 * @param {string} checkedDeadlineDate - The date to check, in 'YYYY-MM-DD' format.
 * @param {Object} pickupLocation - The store location object containing opening hours and exceptions.
 * @param {Object} pickupLocation.opening_hours - An object mapping weekdays to standard opening hours.
 * @param {Array} pickupLocation.exceptional_opening_hours - Array of exceptional opening day objects with `date` and `hours`.
 * @param {string} pickupLocation.timezone - The store's timezone.
 * @param {Array} pickupLocation.special_closures - Array of special closure rules to evaluate.
 * @returns {boolean} - Returns true if the store is available for pickup on the given date, false otherwise.
 */
function isPickupDateAvailable(checkedDeadlineDate, pickupLocation) {
  const checkedDeadline = moment.tz(checkedDeadlineDate, 'YYYY-MM-DD', pickupLocation.timezone)

  const exceptionalOpeningHours = pickupLocation.exceptional_opening_hours || []
  const storeIsExceptionallyClosed = exceptionalOpeningHours.some(checkExceptionalClosures(checkedDeadlineDate))

  const standardOpeningDayHours =
    pickupLocation.opening_hours[checkedDeadline.locale('en').format('dddd').toLowerCase()]
  const storeIsOpenForPickup = isStoreOpenForPickup(checkedDeadlineDate, pickupLocation, standardOpeningDayHours)

  // A store can be exceptionally closed, exceptionally open, or neither — but not both at the same time.
  return !storeIsExceptionallyClosed && storeIsOpenForPickup
}

/**
 * Calculates the maximum pickup deadline by iterating through calendar days,
 * skipping invalid pickup days (e.g., store closures), until the allowed number
 * of pickup days is reached.
 *
 * The function starts from the day after the original order deadline and checks
 * each day to determine if it can be counted toward the pickup deadline extension.
 * It avoids counting the current pickup deadline day unless explicitly allowed.
 *
 * @function searchMaxPickupDeadline
 * @param {Object} pickupLocation - The store location object containing opening hours and exceptions.
 * @param {Object} pickupLocation.opening_hours - An object mapping weekdays to standard opening hours.
 * @param {Array} pickupLocation.exceptional_opening_hours - Array of exceptional opening day objects with `date` and `hours`.
 * @param {string} pickupLocation.timezone - The store's timezone.
 * @param {Array} pickupLocation.special_closures - Array of special closure rules to evaluate.
 * @param {number} nbDaysToPickUp - The number of valid pickup days to add (in days).
 * @param {string|Date|moment.Moment} originalOrderDeadline - The original pickup deadline.
 * @returns {moment.Moment} A moment object representing the computed maximum pickup deadline.
 */
export function searchMaxPickupDeadline(pickupLocation, nbDaysToPickUp, originalOrderDeadline) {
  let searchingDay = moment(originalOrderDeadline).tz(pickupLocation.timezone).add(1, 'days')
  while (nbDaysToPickUp > 0) {
    const checkedDeadlineDate = searchingDay.format('YYYY-MM-DD')

    if (isPickupDateAvailable(checkedDeadlineDate, pickupLocation)) {
      nbDaysToPickUp -= 1
    }

    if (nbDaysToPickUp !== 0) {
      searchingDay.add(1, 'days')
    }
  }

  return searchingDay
}

/**
 * Fetches and maps the pickup location associated with an order.
 *
 * This function checks if the order has a `pickup_location_id`, and if so,
 * retrieves the corresponding location using the provided access token.
 * If the location is successfully fetched, it is passed through `mapLocation`
 * before being returned.
 *
 * @async
 * @function getPickupLocation
 * @param {string} accessToken - The authentication token used to authorize the API request.
 * @param {Object} order - The order object containing the `pickup_location_id`.
 * @param {string} order.pickup_location_id - The ID of the pickup location to fetch.
 * @returns {Promise<Object|undefined>} The mapped pickup location object, or `undefined` if not found or not applicable.
 */
async function getPickupLocation(accessToken, order) {
  if (order?.pickup_location_id) {
    const fetchedPickupLocation = await LocationService.getLocation(accessToken, order.pickup_location_id)
    if (fetchedPickupLocation) {
      return mapLocation(fetchedPickupLocation)
    }
  }
}

/**
 * Maps a raw location object fetched from the API to a simplified internal format.
 *
 * This function extracts and restructures relevant fields from the fetched location
 * to match the expected internal structure used in the application.
 *
 * @function mapLocation
 * @param {Object} fetchedLocation - The raw location object returned by the API.
 * @param {string} fetchedLocation.timezone - The timezone of the location.
 * @param {Object} fetchedLocation.address - The address object of the location.
 * @param {string} fetchedLocation.address.country_id - The country ID of the address.
 * @param {Array} fetchedLocation.opening_hours - Regular opening hours.
 * @param {Array} fetchedLocation.special_closures - List of special closure dates.
 * @param {Array} fetchedLocation.exceptional_opening_hours - Exceptional opening hours.
 * @returns {Object} A simplified location object with selected fields.
 */
function mapLocation(fetchedLocation) {
  return {
    timezone: fetchedLocation.timezone,
    address: {
      country_id: fetchedLocation.address.country_id,
    },
    opening_hours: fetchedLocation.opening_hours,
    special_closures: fetchedLocation.special_closures,
    exceptional_opening_hours: fetchedLocation.exceptional_opening_hours,
  }
}

/**
 * Fetches the pickup location and its corresponding settings based on the order and access token.
 *
 * This function first attempts to retrieve the pickup location using the order's `pickup_location_id`.
 * If a location is found, it then fetches the related settings using the country ID from the location's address.
 * In case of any error during the process, it logs the error and returns `null` values for both fields.
 *
 * @async
 * @function fetchPickupLocationAndSettings
 * @param {string} accessToken - The authentication token used for API requests.
 * @param {Object} order - The order object containing the `pickup_location_id`.
 * @returns {Promise<{pickupLocation: Object|null, pickupLocationSettings: Object|null}>}
 * An object containing the mapped pickup location and its settings, or `null` values if not available.
 */
async function fetchPickupLocationAndSettings(accessToken, order) {
  try {
    const pickupLocation = await getPickupLocation(accessToken, order)

    let pickupLocationSettings = null
    if (pickupLocation) {
      pickupLocationSettings = await OrderSettingsService.get(accessToken, pickupLocation?.address?.country_id)
    }

    return { pickupLocation, pickupLocationSettings }
  } catch (error) {
    console.error('Error fetching pickup location and settings:', error)
    return { pickupLocation: null, pickupLocationSettings: null }
  }
}

/**
 * Applies pickup location data and its settings to a Vue component instance.
 *
 * This function fetches the pickup location and its related settings using the component's
 * authentication token and order data. It then assigns the results to `pickupLocation` and
 * `pickupLocationSettings` on the component instance.
 *
 * ### Required Vue component properties:
 * - `vm.$auth.accessToken` (String): The authentication token used for API requests.
 * - `vm.order` (Object): The order object used to determine the pickup location.
 * - `vm.pickupLocation` (Object): Will be set with the fetched pickup location.
 * - `vm.pickupLocationSettings` (Object): Will be set with the fetched settings for the pickup location.
 *
 * @async
 * @function applyPickupLocationToComponent
 * @param {Vue} vm - The Vue component instance (typically `this` inside a component).
 * @param {String} vm.$auth.accessToken The authentication token used for API requests.
 * @param {Object} vm.order The order object used to determine the pickup location.
 * @param {Object} vm.order.pickup_location_id The pickup location of the order.
 * @param {Object} vm.pickupLocation Will be set with the fetched pickup location.
 * @param {Object} vm.pickupLocationSettings Will be set with the fetched settings for the pickup location.
 * @returns {Promise<void>} Resolves when the data has been fetched and assigned.
 */
export async function applyPickupLocationToComponent(vm) {
  const { pickupLocation, pickupLocationSettings } = await fetchPickupLocationAndSettings(
    vm.$auth.accessToken,
    vm.order
  )

  vm.pickupLocation = pickupLocation
  vm.pickupLocationSettings = pickupLocationSettings
}

/**
 * Computes the maximum pickup deadline based on original and current deadlines,
 * pickup location settings, and extension limits.
 *
 * @param {Object} order - The order object containing deadlines.
 * @param {Object} pickupLocationSettings - Settings that include the extension limit.
 * @param {Object} pickupLocation - The pickup location object.
 * @returns {string|undefined} - The computed max pickup deadline in 'YYYY-MM-DD' format, or undefined.
 */
export function computeMaxExtensionDate(order, pickupLocationSettings, pickupLocation) {
  const originalDeadline = order?.original_pickup_deadline
  const delaysSettings = pickupLocationSettings?.delays
  let pickupDeadlineExtensionLimit = delaysSettings?.global?.pickup_timeout?.pickup_deadline_extension_limit

  if(order.type === "click_and_reserve") {
    pickupDeadlineExtensionLimit = delaysSettings?.click_and_reserve?.pickup_timeout?.pickup_deadline_extension_limit
  }

  if (pickupDeadlineExtensionLimit) {
    const deadlineExtensionLimitInDays = pickupDeadlineExtensionLimit / 86400
    const openingHours = Object.values(pickupLocation.opening_hours)
    if(!openingHours.some(hour => hour.length !== 0)){
      return undefined
    }

    return searchMaxPickupDeadline(pickupLocation, deadlineExtensionLimitInDays, originalDeadline).format(
      'YYYY-MM-DD'
    )
  }

  return undefined
}

/**
 * Determines whether the "Modify Pickup Deadline" button should be displayed.
 *
 * This function checks if the pickup deadline extension feature is activated and within limits,
 * and whether the current user has the appropriate role (unless role check is skipped).
 * It also verifies that the order is in the "to_pickup" status.
 *
 * @param {Object} pickupLocationSettings - Settings related to pickup deadlines and role-based display permissions.
 * @param {boolean} skipRoleCheck - If true, bypasses role-based permission checks.
 * @param {string[]} userRoles - List of roles assigned to the current user.
 * @param {Object} [order={}] - The order object, used to check the current status.
 * @param {string} [order.current_status] - The current status of the order (e.g., "to_pickup").
 * @param {string} [order.type] - The order type (e.g., "click_and_reserve").
 * @param {string} [order.pickup_deadline] - The current order pickup deadline.
 * @returns {boolean} - Returns true if the button should be displayed, false otherwise.
 */
export function canDisplayPickupDeadlineButton(pickupLocationSettings, skipRoleCheck, userRoles, order = {}) {
  const allowedRoles = pickupLocationSettings?.display?.display_pickup_deadline_extension_button
  const globalPickupTimeout = pickupLocationSettings?.delays?.global?.pickup_timeout
  const clickAndReservePickupTimeout = pickupLocationSettings?.delays?.click_and_reserve?.pickup_timeout

  let deadlineActivated = globalPickupTimeout?.activate
  let extensionLimit = globalPickupTimeout?.pickup_deadline_extension_limit
  let extensionActivated = globalPickupTimeout?.activate_pickup_deadline_extension
  if (order.type === "click_and_reserve") {
    deadlineActivated = clickAndReservePickupTimeout?.activate
    extensionLimit = clickAndReservePickupTimeout?.pickup_deadline_extension_limit
    extensionActivated = clickAndReservePickupTimeout?.activate_pickup_deadline_extension
  }

  const pickupDeadlineIsModifiable = order.pickup_deadline && extensionActivated && extensionLimit
  const userIsAllowedToModify = skipRoleCheck || allowedRoles?.some((role) => userRoles.includes(role))

  return (
    deadlineActivated && pickupDeadlineIsModifiable && userIsAllowedToModify && order.current_status === 'to_pickup'
  )
}

/**
 * Updates the pickup deadline of an order.
 *
 * This function takes a date string in the format `YYYY-MM-DD`, converts it to a `Date` object,
 * sets the time to 23:59:59 to include the entire day, and sends the updated deadline
 * to the API using `OrderService.patch`.
 *
 * @async
 * @function updatePickupDeadline
 * @param {string} accessToken - Access token used for authenticating the API request.
 * @param {string|number} orderId - Unique identifier of the order to update.
 * @param {string} newDeadline - New pickup deadline in `YYYY-MM-DD` format.
 * @returns {Promise<Object>} - The result of the PATCH request to the API.
 *
 * @throws {Error} - Throws an error if the API request fails.
 */
export async function updatePickupDeadline(accessToken, orderId, newDeadline) {
  const newDeadlineAsDate = new Date(newDeadline)
  newDeadlineAsDate.setHours(23, 59, 59, 0)

  return await OrderService.patch(accessToken, orderId, { pickup_deadline: newDeadlineAsDate.toISOString() })
}

/**
 * Checks if the maximum extension date has been reached or passed
 * compared to the current pickup deadline or if it is in the past.
 *
 * @param {string|Date} maxExtensionDate - The maximum date to which an extension is allowed.
 * @param {string|Date} currentPickupDeadline - The current deadline for pickup.
 * @returns {boolean} - Returns true if the max extension date is the same or before the pickup deadline.
 */
export function maxExtensionDateReached(maxExtensionDate, currentPickupDeadline) {
  const maxDeadlineIsInThePast = moment(maxExtensionDate).isBefore(moment())
  const maxDeadlineReached = moment(maxExtensionDate).isSameOrBefore(moment(currentPickupDeadline))
  return maxDeadlineReached || maxDeadlineIsInThePast
}
